import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { authAPI } from "@/services/api";
import { isFirebaseConfigured, sendPhoneOtp, confirmPhoneOtp } from "@/lib/firebase";

const COUNTRY_OPTIONS = [
  { code: "+234", label: "Nigeria (+234)", placeholder: "8012345678", maxLength: 11 },
  { code: "+1", label: "United States (+1)", placeholder: "6505553434", maxLength: 10 },
];

/**
 * Firebase phone OTP for BRM sign-in or phone linking during onboarding.
 * Uses invisible reCAPTCHA (required by Firebase before SMS is sent).
 */
const FirebasePhoneAuth = ({
  role = "brm",
  referralCode = null,
  onSession,
  linkOnly = false,
  onLinked,
}) => {
  const [countryCode, setCountryCode] = useState("+234");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("phone");
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const country =
    COUNTRY_OPTIONS.find((option) => option.code === countryCode) || COUNTRY_OPTIONS[0];

  const formatPhone = (value) => value.replace(/\D/g, "");

  const getFullPhone = () => {
    const clean = formatPhone(phone);
    if (countryCode === "+1") {
      return `+1${clean}`;
    }
    if (clean.startsWith("234")) return `+${clean}`;
    if (clean.startsWith("0")) return `+234${clean.slice(1)}`;
    return `+234${clean}`;
  };

  const exchangeToken = async (idToken) => {
    if (linkOnly && onLinked) {
      await authAPI.linkBrmPhone({ id_token: idToken });
      onLinked(getFullPhone());
      return;
    }
    const session = await authAPI.firebaseSession({
      id_token: idToken,
      role,
      referral_code: referralCode || undefined,
    });
    onSession(session);
  };

  const handleSendOtp = async () => {
    if (!isFirebaseConfigured()) {
      toast.error("Firebase is not configured");
      return;
    }
    const minDigits = countryCode === "+1" ? 10 : 10;
    if (!phone || phone.length < minDigits) {
      toast.error("Enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      const conf = await sendPhoneOtp(getFullPhone(), "firebase-send-otp");
      setConfirmation(conf);
      setStep("otp");
      toast.success("Verification code sent");
    } catch (error) {
      toast.error(error.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6 || !confirmation) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const idToken = await confirmPhoneOtp(confirmation, otp);
      await exchangeToken(idToken);
      toast.success(linkOnly ? "Phone verified" : "Signed in successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  if (!isFirebaseConfigured()) {
    return null;
  }

  return (
    <div className="space-y-4">
      {step === "phone" ? (
        <>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Country</Label>
            <select
              value={countryCode}
              onChange={(e) => {
                setCountryCode(e.target.value);
                setPhone("");
              }}
              className="w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900"
              data-testid="firebase-phone-country"
            >
              {COUNTRY_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Phone number</Label>
            <div className="phone-input-group">
              <span className="phone-input-prefix">{countryCode}</span>
              <input
                type="tel"
                placeholder={country.placeholder}
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                className="phone-input-field"
                maxLength={country.maxLength}
                data-testid="firebase-phone-input"
              />
            </div>
            <p className="text-xs text-gray-500">
              Standard SMS rates may apply. Firebase uses invisible reCAPTCHA before sending a code.
            </p>
          </div>
          <Button
            type="button"
            id="firebase-send-otp"
            className="w-full h-12"
            onClick={handleSendOtp}
            disabled={loading}
            data-testid="firebase-send-otp"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Send verification code
          </Button>
        </>
      ) : (
        <>
          <p className="text-center text-sm text-gray-500">
            Code sent to <span className="font-medium text-gray-900">{getFullPhone()}</span>
          </p>
          <div className="flex justify-center">
            <InputOTP value={otp} onChange={setOtp} maxLength={6}>
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button
            type="button"
            className="w-full h-12"
            onClick={handleVerifyOtp}
            disabled={loading || otp.length !== 6}
            data-testid="firebase-verify-otp"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Verify & continue
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => {
              setStep("phone");
              setOtp("");
              setConfirmation(null);
            }}
          >
            Change number
          </Button>
        </>
      )}
    </div>
  );
};

export default FirebasePhoneAuth;
